import React from "react"
import { graphql } from "gatsby"
import Img from "gatsby-image"
import { withTheme } from "emotion-theming"
import { readableColor } from "polished"

import Layout from "@layouts/base"

import { FoodCollected, VolunteerParticipation } from "@components/charts"
import { Container, Row, Col } from "@components/grid"
import Infographic from "@components/infographic"
import { KeylineDash } from "@components/keyline"
import Panel from "@components/panel"
import Player from "@components/player"
import { Caption, Heading, Pullquote } from "@components/type"

import { Car, Carrot, Lights, Utensils } from "@svgs"

import charlotte from "@audio/charlotte.m4a"

const Emergency = ({ theme, data }) => {
  const pageTitle = "Emergency Food Services"
  const border = `3px dashed ${theme.colours.secondary}`
  return (
    <Layout image={data.hero.childImageSharp.fluid} title={pageTitle}>
      <Container>
        <Heading colour={theme.colours.primary}>{pageTitle}</Heading>
        <Player
          title="Charlotte & Shane – Emergency Food Department Podcast"
          src={charlotte}
        />
        <p>
          Neighbour to Neighbour Centre (N2N) began more than 30 years ago as a
          small food bank and was built on the fundamental principle of helping
          those in need on Hamilton Mountain.
        </p>

        <p>
          Our food bank is the only emergency food provider on Hamilton
          Mountain. Our food programs have evolved into an indispensable part of
          our community’s social fabric. Neighbour to Neighbour works to improve
          the quality of life in our community by offering emergency food
          through 3 programs: Food Bank, Home Delivery and our Christmas Market
          program.
        </p>
      </Container>
      <Panel background={theme.colours.primary} spaced>
        <Caption scale={2 / 3} align="center">
          "&hellip;thank you &hellip; I was at my lowest point&hellip; and I am
          leaving with a full grocery cart&hellip;"
        </Caption>
      </Panel>
      <Container>
        <h2>Our Community Impact</h2>
        <Caption>Impact of your donation</Caption>
        <Pullquote colour={theme.colours.primary}>
          For every $ donated to Neighbour to Neighbour, we can distribute $11
          worth of food in the community.
        </Pullquote>
        <Row>
          <Col
            width={[1, 1, 1 / 2]}
            style={{
              borderRight: border,
              marginBottom: 0,
            }}
          >
            <Infographic
              icon={<Carrot />}
              value="Food Collected"
              colour={theme.colours.secondary}
            >
              <strong>926,976</strong> Pounds of food collected
              <br />
              <strong> 36%</strong> Is produce, meat and dairy food collected
              valued at <strong>$2,410,130 </strong>
              <br />
              <strong>2,325</strong> Pounds of food donated to the Community
              Food Centre
            </Infographic>
          </Col>
          <Col width={[1, 1, 1 / 2]}>
            <Infographic
              icon={<Utensils />}
              value="People Served"
              colour={theme.colours.danger}
            >
              <strong>13,825</strong> Units of service <br />
              <strong>1,152</strong> Families per month <br />
              <strong>9,089</strong> Individuals <br />
              <strong>23,464</strong> Families served since 1986
              <br />
              <strong>8%</strong> Of clients are seniors
            </Infographic>
          </Col>
        </Row>
        <KeylineDash colour={theme.colours.secondary} />
        <Row>
          <Col width={[1, 1, 1 / 2]} style={{ borderRight: border }}>
            <Infographic
              icon={<Lights />}
              value="Christmas Market  Program"
              colour={theme.colours.success}
            >
              <strong>1,450</strong> Households attended our Christmas Market
              which ensures milk, eggs, meat and <strong>3</strong> vegetables
              to every household in addition to non-perishables
            </Infographic>
          </Col>
          <Col width={[1, 1, 1 / 2]}>
            <Infographic
              icon={<Car />}
              value="Home Delivery"
              colour={theme.colours.primary}
            >
              <strong>481</strong> Home Deliveries were made to clients who are
              unable to access our food bank due to medical challenges.
            </Infographic>
          </Col>
        </Row>
      </Container>
      <Panel>
        <h2>Food Collected | 2016 - 2019</h2>
        <FoodCollected />
      </Panel>
      <Panel background={theme.color}>
        <h2>Volunteers</h2>
        <Caption scale={1 / 2}>
          13,852 hours contributed by the food bank and warehouse team
        </Caption>
        <VolunteerParticipation colour={readableColor(theme.color)} />
        <Caption>
          Emergency Food volunteer participation increased by 15% over 3 years
        </Caption>
      </Panel>
      <Panel background={theme.colours.primary}>
        <Caption scale={2 / 3} align="center">
          "Neighbour to Neighbour allows me to have a choice when I shop and the
          volunteers treat me with dignity and respect."
        </Caption>
      </Panel>
      <Panel>
        <Row>
          <Col width={1 / 2}>
            <Img fluid={data.girls.childImageSharp.fluid} alt="" />
          </Col>
          <Col width={1 / 2}>
            <Img fluid={data.veggies.childImageSharp.fluid} alt="" />
          </Col>
        </Row>
        <Caption scale={2 / 3} align="center">
          "&hellip;our family thanks you for your kindness and generosity during
          this holiday season. The food that you have provided is very much
          appreciated and will not be forgotten.""
        </Caption>
      </Panel>
    </Layout>
  )
}

export const query = graphql`
  query {
    hero: file(relativePath: { eq: "hero/emergency.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    veggies: file(relativePath: { eq: "emergency/vegetables.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 475) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    girls: file(relativePath: { eq: "emergency/girls.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 475) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`

export default withTheme(Emergency)
